import {
  safeLocalStorageGet,
  safeLocalStorageSet,
} from '../storage/safeLocalStorage';

export const STALE_CHUNK_RECOVERY_STORAGE_KEY = 'hxStaleChunkRecoveredVersion';

export type RecoverFromStaleChunkOptions = {
  /** Version of the bundle currently running, used as the one-shot guard. */
  appVersion: string;
  /**
   * Where the customer was heading. Defaults to the current URL. Ignored if it
   * is not same-origin.
   */
  targetHref?: string;
};

/**
 * Whether this bundle version has already forced a recovery load.
 *
 * Keyed on the running bundle's version rather than a plain boolean: a
 * successful recovery lands the tab on the newly deployed bundle, whose version
 * differs, so the guard clears itself. A genuinely missing chunk — a broken
 * deploy, where reloading returns the same version — cannot loop.
 */
export const hasRecoveredFromStaleChunk = (appVersion: string): boolean =>
  safeLocalStorageGet(STALE_CHUNK_RECOVERY_STORAGE_KEY) === appVersion;

const resolveSameOriginHref = (targetHref: string | undefined): string => {
  if (!targetHref) return window.location.href;

  try {
    const resolved = new URL(targetHref, window.location.origin);
    return resolved.origin === window.location.origin
      ? resolved.href
      : window.location.href;
  } catch {
    return window.location.href;
  }
};

/**
 * Leave the stale bundle behind by handing the navigation to the browser.
 *
 * Returns whether a load was started, so callers can render a recovering state
 * instead of an error when it was.
 */
export const recoverFromStaleChunk = ({
  appVersion,
  targetHref,
}: RecoverFromStaleChunkOptions): boolean => {
  if (typeof window === 'undefined') return false;
  if (hasRecoveredFromStaleChunk(appVersion)) return false;

  safeLocalStorageSet(STALE_CHUNK_RECOVERY_STORAGE_KEY, appVersion);

  // A hard navigation, not `location.reload()`: the import fails while moving to
  // a new route, so reloading would re-render the page the customer is leaving.
  // Sending the browser to the destination has the server render it against the
  // new bundle, and they land where they clicked.
  const destination = resolveSameOriginHref(targetHref);

  if (destination === window.location.href) {
    window.location.reload();
  } else {
    window.location.assign(destination);
  }

  return true;
};
